/**
 * Admin User Detail Page
 *
 * Full user profile view with management actions:
 * - Update subscription tier
 * - Set or clear a custom monthly price
 * - Enable / disable account
 * - Reset password
 * - Recalculate streaks from publishing history
 */

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowsClockwise,
  Calendar,
  Clock,
  CurrencyDollar,
  FileText,
  Fire as Flame,
  type Icon,
  Key,
  Stack as Layers,
  LinkSimple,
  Envelope as Mail,
  EnvelopeOpen as MailCheck,
  EnvelopeSimple as MailX,
  Megaphone,
  Shield,
  Snowflake,
  Sparkle,
  Star,
  Trophy,
  User,
  UserCheck,
  UsersThree,
  UserMinus as UserX,
  Warning,
  XLogo,
  Lightning as Zap,
} from "@phosphor-icons/react";
import {
  Alert,
  PageHeader,
  SegmentedControl,
  StatTile,
  type StatTone,
  StatusPill,
} from "@charisol-spark/ui";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Progress } from "@/components/ui/Progress";
import { Spinner } from "@/components/ui/Spinner";
import {
  useAdminUser,
  useDisableAdminUser,
  useRecalculateStreaks,
  useResetAdminPassword,
  useUpdateAdminUser,
  useUpdateAdminUserRoles,
} from "@/hooks/useAdmin";
import { cn } from "@/utils/cn";
import { getCurrentUserEmail, hasRole } from "@/components/auth/AdminGuard";
import { BADGE_MAP, getBadgeCategory, getBadgeName } from "@/config/badges";
import {
  type AdminActivityEntry,
  type AdminUserDetail,
  type AdminUserPersona,
  COST_NOT_REPORTED,
  type PlatformRoleName,
} from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { UserCreditsPanel } from "./UserCreditsPanel";
import { formatMicroUsd, formatPeriod } from "@/utils/formatting";
import {
  CUSTOM_PRICE_CAP_USD,
  fromMicroUsd,
  toMicroUsd,
} from "@charisol-spark/billing";

const TIERS = ["free", "pro", "team", "enterprise"] as const;

/** Team-size answers from onboarding, as the user saw them. */
const TEAM_SIZE_LABELS: Record<string, string> = {
  "just-me": "Just me",
  "2-10": "2–10",
  "11-50": "11–50",
  "50+": "50+",
};

const SECTION_CLASS =
  "space-y-3 rounded-xl border border-border/50 bg-card p-5";
const SECTION_TITLE_CLASS = "text-sm font-semibold";
const CONFIRM_CLASS =
  "space-y-3 rounded-xl border border-amber-300/50 bg-amber-50 p-4 dark:border-amber-600/30 dark:bg-amber-900/10";

function formatDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Small info row used in the profile card. */
function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div>
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <div className="mt-0.5">{value}</div>
      </div>
    </div>
  );
}

/** Stat block for entity counts. */
function CountCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon: Icon;
  tone: StatTone;
}) {
  return <StatTile label={label} value={value} icon={icon} tone={tone} />;
}

/**
 * A USD amount typed by an admin, as integer micro-dollars, or why not.
 *
 * Refused rather than rounded: the figure decides what the margin report
 * says the account brings in, and the API refuses anything it would have to
 * coerce, so say so here first.
 */
function parseUsdInput(
  raw: string
): { ok: true; microUsd: number } | { ok: false; message: string } {
  const text = raw.trim().replace(/^\$/, "");
  if (text === "")
    return { ok: false, message: "Enter a monthly price in USD." };
  if (!/^\d+(\.\d{1,2})?$/.test(text)) {
    return {
      ok: false,
      message: "Enter a whole number of dollars, or dollars and cents.",
    };
  }
  const usd = Number(text);
  if (usd > CUSTOM_PRICE_CAP_USD) {
    return {
      ok: false,
      message: `The most a custom price can be is $${CUSTOM_PRICE_CAP_USD.toLocaleString()} a month.`,
    };
  }
  return { ok: true, microUsd: toMicroUsd(usd) };
}

/**
 * What this account is charged each month, and a form to change it.
 *
 * The list price applies unless an admin sets a custom one here: a discount,
 * or $0 for a trial. Clearing it returns the account to the list price.
 */
function PricePanel({
  user,
  onSave,
  onClear,
  isPending,
}: {
  user: AdminUserDetail;
  onSave: (priceMicroUsd: number, note: string) => void;
  onClear: () => void;
  isPending: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [usdInput, setUsdInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [inputError, setInputError] = useState<string | null>(null);

  // Absent from an API older than custom prices; see COST_NOT_REPORTED.
  const price = user.price as AdminUserDetail["price"] | undefined;
  if (!price) {
    return (
      <p className="text-xs text-muted-foreground">
        This environment&apos;s API does not report prices yet. It will once the
        backend is deployed.
      </p>
    );
  }

  const override = user.billing ?? null;
  const tierName =
    user.subscriptionTier.charAt(0).toUpperCase() +
    user.subscriptionTier.slice(1);

  function startEditing() {
    setUsdInput(
      price?.priceMicroUsd !== null && price?.priceMicroUsd !== undefined
        ? String(fromMicroUsd(price.priceMicroUsd))
        : ""
    );
    setNoteInput(override?.note ?? "");
    setInputError(null);
    setEditing(true);
  }

  function submit() {
    const parsed = parseUsdInput(usdInput);
    if (!parsed.ok) {
      setInputError(parsed.message);
      return;
    }
    setInputError(null);
    onSave(parsed.microUsd, noteInput.trim());
    setEditing(false);
  }

  return (
    <div className="space-y-3 border-t border-border/50 pt-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs font-medium text-muted-foreground">
          Monthly price
        </p>
        {!editing && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={startEditing}
              disabled={isPending}
            >
              {price.source === "override" ? "Change" : "Set custom price"}
            </Button>
            {price.source === "override" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onClear}
                disabled={isPending}
              >
                {isPending ? "Saving..." : "Use list price"}
              </Button>
            )}
          </div>
        )}
      </div>

      <p className="text-xl font-bold tabular-nums">
        {price.priceMicroUsd === null
          ? "No list price"
          : formatMicroUsd(price.priceMicroUsd)}
      </p>
      <p className="text-xs text-muted-foreground">
        {price.source === "override" ? (
          <>
            Custom price
            {override?.setAt && ` set ${formatDate(override.setAt)}`}
            {override?.setBy && ` by ${override.setBy}`}. The {tierName} list
            price does not apply.
            {override?.note && (
              <span className="mt-1 block italic">
                &ldquo;{override.note}&rdquo;
              </span>
            )}
          </>
        ) : price.priceMicroUsd === null ? (
          <>
            {tierName} has no list price. Set a custom price so this account
            counts towards revenue and margin.
          </>
        ) : (
          <>
            {tierName} list price. Set a custom price for a discount or a trial.
          </>
        )}
      </p>

      {editing && (
        <form
          className="space-y-3 rounded-xl border border-border/50 bg-muted/30 p-4"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="custom-price-usd">Monthly price (USD)</Label>
            <Input
              id="custom-price-usd"
              inputMode="decimal"
              placeholder="0.00"
              value={usdInput}
              onChange={(event) => {
                setUsdInput(event.target.value);
                if (inputError) setInputError(null);
              }}
              error={inputError ?? undefined}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              0 for a trial. Up to ${CUSTOM_PRICE_CAP_USD.toLocaleString()}.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="custom-price-note">Note (optional)</Label>
            <Input
              id="custom-price-note"
              placeholder="Why: trial until March, founder discount..."
              maxLength={500}
              value={noteInput}
              onChange={(event) => setNoteInput(event.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" type="submit" disabled={isPending}>
              {isPending ? "Saving..." : "Save price"}
            </Button>
            <Button
              size="sm"
              type="button"
              variant="ghost"
              onClick={() => setEditing(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/** Every Cognito group this console can grant, in display order. */
const ALL_PLATFORM_ROLES: PlatformRoleName[] = [
  "platform_admin",
  "platform_support",
  "platform_analyst",
];

/** "platform_support" -> "Support" for display. */
function roleLabel(role: PlatformRoleName): string {
  const short = role.replace("platform_", "");
  return short.charAt(0).toUpperCase() + short.slice(1);
}

/**
 * Platform roles — checkboxes for the three Cognito groups plus a
 * Save-then-confirm flow. `platform_admin` viewers only (gated by the
 * caller); the server enforces every safeguard below again regardless (see
 * admin/users/roles.ts) — this is just what keeps an admin from firing a
 * request that will be refused anyway, and from removing their own access
 * by mistake.
 *
 * Keyed by the caller on `user.roles` so a successful save (which refetches
 * the user) remounts this with fresh `checked` state, rather than needing
 * its own effect to resync local state to a prop that changed underneath it.
 */
function PlatformRolesPanel({
  user,
  onSave,
  isPending,
}: {
  user: AdminUserDetail;
  /** `onDone(true)` only on success — the dialog stays open on failure,
   * matching the Account Actions confirm dialogs below. */
  onSave: (
    add: PlatformRoleName[],
    remove: PlatformRoleName[],
    onDone: (success: boolean) => void
  ) => void;
  isPending: boolean;
}) {
  const currentRoles = new Set(user.roles as PlatformRoleName[]);
  const [selected, setSelected] = useState<Set<PlatformRoleName>>(
    new Set(currentRoles)
  );
  const [confirming, setConfirming] = useState(false);

  const viewerEmail = getCurrentUserEmail();
  const isSelf = Boolean(
    viewerEmail && viewerEmail.toLowerCase() === user.email.toLowerCase()
  );
  // Mirrors the server's own refusal (admin/users/roles.ts) so the request
  // never has to round-trip to learn this — the server enforces it anyway.
  const ownAdminRoleLocked = isSelf && currentRoles.has("platform_admin");

  function toggle(role: PlatformRoleName) {
    if (role === "platform_admin" && ownAdminRoleLocked) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(role)) next.delete(role);
      else next.add(role);
      return next;
    });
  }

  const added = ALL_PLATFORM_ROLES.filter(
    (r) => selected.has(r) && !currentRoles.has(r)
  );
  const removed = ALL_PLATFORM_ROLES.filter(
    (r) => !selected.has(r) && currentRoles.has(r)
  );
  const hasChanges = added.length > 0 || removed.length > 0;

  function handleConfirm() {
    onSave(added, removed, (success) => {
      if (success) setConfirming(false);
    });
  }

  return (
    <div className={SECTION_CLASS}>
      <h2 className={SECTION_TITLE_CLASS}>Platform roles</h2>
      <div className="space-y-2">
        {ALL_PLATFORM_ROLES.map((role) => {
          const locked = role === "platform_admin" && ownAdminRoleLocked;
          return (
            <label
              key={role}
              className={cn(
                "flex items-center gap-2 text-sm",
                locked && "opacity-60"
              )}
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border/50"
                checked={selected.has(role)}
                disabled={isPending || confirming || locked}
                onChange={() => toggle(role)}
              />
              {roleLabel(role)}
              {locked && (
                <span className="text-xs text-muted-foreground">
                  (you can&apos;t remove your own admin role)
                </span>
              )}
            </label>
          );
        })}
      </div>

      {hasChanges && !confirming && (
        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            onClick={() => setConfirming(true)}
            disabled={isPending}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelected(new Set(currentRoles))}
            disabled={isPending}
          >
            Cancel
          </Button>
        </div>
      )}

      {confirming && (
        <div className={CONFIRM_CLASS}>
          <p className="text-sm font-medium">
            {added.length > 0 && `Add: ${added.map(roleLabel).join(", ")}. `}
            {removed.length > 0 &&
              `Remove: ${removed.map(roleLabel).join(", ")}. `}
            This user will be signed out everywhere so the change takes effect.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="destructive"
              onClick={handleConfirm}
              disabled={isPending}
            >
              {isPending ? "Saving..." : "Confirm"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirming(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Bar colour for a metered allowance: past the limit means in grace. */
function meterVariant(used: number, limit: number) {
  if (used > limit) return "error" as const;
  return limit > 0 && used / limit >= 0.8
    ? ("warning" as const)
    : ("default" as const);
}

function MeterBlock({
  icon: Icon,
  label,
  figure,
  used,
  limit,
  children,
}: {
  icon: React.ElementType;
  label: string;
  figure: string;
  /** Leave out, or pass a `null` limit, for no bar. */
  used?: number;
  limit?: number | null;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Icon className="h-4 w-4" aria-hidden="true" />
        {label}
      </div>
      <p className="text-xl font-bold tabular-nums">{figure}</p>
      {used !== undefined && limit !== undefined && limit !== null && (
        <Progress
          value={limit > 0 ? (used / limit) * 100 : 100}
          variant={meterVariant(used, limit)}
        />
      )}
      <div className="space-y-0.5 text-xs text-muted-foreground">
        {children}
      </div>
    </div>
  );
}

/**
 * This month's metered usage and what it cost us, from the quota meters and
 * the cost ledger: the same numbers enforcement reads.
 */
function UsageAndCostPanel({ user }: { user: AdminUserDetail }) {
  // Absent from an API older than cost tracking; see COST_NOT_REPORTED.
  const quota = user.quota as AdminUserDetail["quota"] | undefined;
  const cost = user.costThisMonth as
    | AdminUserDetail["costThisMonth"]
    | undefined;

  if (!quota || !cost) {
    return (
      <div className="rounded-xl border border-border/50 bg-card p-5 text-sm text-muted-foreground">
        {COST_NOT_REPORTED}
      </div>
    );
  }

  const resets = new Date(quota.resetsAt).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  const { x } = quota;

  let generationsNote: string;
  if (quota.limit === null) {
    generationsNote = "Unlimited on this plan.";
  } else if (quota.used > quota.limit) {
    generationsNote = `In grace: ${(quota.used - quota.limit).toLocaleString()} past the limit.`;
  } else {
    generationsNote = `${(quota.limit - quota.used).toLocaleString()} left.`;
  }
  if (quota.bonus > 0) {
    generationsNote += ` Includes a +${quota.bonus} streak bonus.`;
  }

  let xNote: string;
  if (x.budgetMicroUsd === null || x.limitPosts === null) {
    xNote = "Unlimited on this plan.";
  } else if (x.usedMicroUsd > x.budgetMicroUsd) {
    xNote = "In grace: past this month's X budget.";
  } else {
    // postsRemaining counts every post as a link post, the dearest kind.
    xNote = `At least ${(x.postsRemaining ?? 0).toLocaleString()} of ${x.limitPosts.toLocaleString()} posts left.`;
  }

  return (
    <div className={cn(SECTION_CLASS, "space-y-4")}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className={SECTION_TITLE_CLASS}>
          Usage & Cost · {formatPeriod(quota.period)}
        </h2>
        <p className="text-xs text-muted-foreground">
          <span className="capitalize">{quota.tier}</span> plan · resets{" "}
          {resets}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <MeterBlock
          icon={Zap}
          label="AI generations"
          figure={
            quota.limit === null
              ? quota.used.toLocaleString()
              : `${quota.used.toLocaleString()} / ${quota.limit.toLocaleString()}`
          }
          used={quota.used}
          limit={quota.limit}
        >
          <p>{generationsNote}</p>
          <p>
            {user.usage.totalGenerations.toLocaleString()} since metering began.
          </p>
          {/* Absent from an API older than credits — the generation figures
              above still work on their own, so this is additive, not a
              replacement. */}
          {quota.credits && (
            <p>
              {quota.credits.used.toLocaleString()}
              {quota.credits.limit !== null &&
                ` / ${quota.credits.limit.toLocaleString()}`}{" "}
              credits
              {quota.credits.bonus > 0 && ` (+${quota.credits.bonus} bonus)`}
            </p>
          )}
        </MeterBlock>

        <MeterBlock
          icon={XLogo}
          label="X publishing"
          figure={
            x.budgetMicroUsd === null
              ? formatMicroUsd(x.usedMicroUsd)
              : `${formatMicroUsd(x.usedMicroUsd)} / ${formatMicroUsd(x.budgetMicroUsd)}`
          }
          used={x.usedMicroUsd}
          limit={x.budgetMicroUsd}
        >
          <p>{xNote}</p>
        </MeterBlock>

        <MeterBlock
          icon={CurrencyDollar}
          label="Cost to us"
          figure={formatMicroUsd(cost.totalMicroUsd)}
        >
          <p className="tabular-nums">
            AI {formatMicroUsd(cost.aiMicroUsd)} · X{" "}
            {formatMicroUsd(cost.xMicroUsd)}
          </p>
          {(cost.unpricedCalls > 0 || cost.fallbackCalls > 0) && (
            <p className="flex items-center gap-1 text-amber-700 dark:text-amber-300">
              <Warning className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Not exact: some calls have no listed rate.
            </p>
          )}
          <p>Gemini and X only. Hosting is not included.</p>
        </MeterBlock>
      </div>
    </div>
  );
}

/**
 * One persona's connected accounts — username and dates only, never a
 * token: the API never sends one either (see admin.ts's ConnectionSummary).
 */
function PersonasPanel({ personas }: { personas: AdminUserPersona[] }) {
  if (personas.length === 0) {
    return <p className="text-sm text-muted-foreground">No personas yet.</p>;
  }

  return (
    <ul className="space-y-3">
      {personas.map((persona) => (
        <li key={persona.id} className="rounded-xl border border-border/50 p-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="font-medium">{persona.name}</p>
            {persona.isDefault && (
              <StatusPill tone="orange">Default</StatusPill>
            )}
            <StatusPill tone={persona.isActive ? "green" : "neutral"}>
              {persona.isActive ? "Active" : "Inactive"}
            </StatusPill>
          </div>

          {persona.connections.length === 0 ? (
            <p className="mt-1.5 text-xs text-muted-foreground">
              No platform connections.
            </p>
          ) : (
            <ul className="mt-2 space-y-1.5">
              {persona.connections.map((conn) => (
                <li
                  key={`${persona.id}-${conn.platform}-${conn.username}`}
                  className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-xs"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <LinkSimple
                      className="h-3 w-3 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <span className="font-medium capitalize">
                      {conn.platform}
                    </span>
                    <span className="text-muted-foreground">
                      @{conn.username || conn.displayName || "unknown"}
                    </span>
                    {!conn.isActive && (
                      <span className="text-muted-foreground">
                        (disconnected)
                      </span>
                    )}
                  </span>
                  <span className="text-muted-foreground">
                    Connected {formatDate(conn.connectedAt)}
                    {conn.lastUsedAt &&
                      ` · last used ${formatDate(conn.lastUsedAt)}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}

/** A short, human label for one activity-feed entry. */
function activityTitle(entry: AdminActivityEntry): string {
  return entry.type === "generation"
    ? "Generated content"
    : (entry.action ?? entry.eventType);
}

/**
 * The user's last 20 audit rows and generations, newest first — mixed
 * together with icons that tell the two kinds apart at a glance.
 */
function RecentActivityPanel({ entries }: { entries: AdminActivityEntry[] }) {
  if (entries.length === 0) {
    return <p className="text-sm text-muted-foreground">No recent activity.</p>;
  }

  return (
    <ul className="space-y-2">
      {entries.map((entry, i) => (
        <li
          key={`${entry.type}-${entry.timestamp}-${i}`}
          className="flex items-start gap-3 rounded-xl border border-border/50 p-3 text-sm"
        >
          <div
            className={cn(
              "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg",
              entry.type === "generation"
                ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                : "bg-muted text-muted-foreground"
            )}
          >
            {entry.type === "generation" ? (
              <Sparkle className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Shield className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium">
              {activityTitle(entry)}
              {entry.type === "generation" && entry.title && (
                <span className="font-normal text-muted-foreground">
                  {" "}
                  — {entry.title}
                </span>
              )}
            </p>
            <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
              <span>{formatDate(entry.timestamp)}</span>
              {entry.type === "generation" && entry.platform && (
                <span className="capitalize">{entry.platform}</span>
              )}
              {entry.type === "generation" && entry.status && (
                <span className="capitalize">{entry.status}</span>
              )}
              {entry.type === "audit" && entry.targetType && (
                <span>on {entry.targetType}</span>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function AdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const { data: user, isLoading, error } = useAdminUser(userId!);
  const updateUser = useUpdateAdminUser();
  const disableUser = useDisableAdminUser();
  const resetPassword = useResetAdminPassword();
  const recalculateStreaks = useRecalculateStreaks();
  const updateRoles = useUpdateAdminUserRoles();

  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    "disable" | "enable" | "reset" | null
  >(null);
  const [confirmRecalculate, setConfirmRecalculate] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Auto-clear status messages after 4 seconds
  const showStatus = (type: "success" | "error", text: string) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 4000);
  };

  // Derive active tier from selection or user data
  const activeTier = selectedTier ?? user?.subscriptionTier ?? "free";

  function handleTierSave() {
    if (!userId || !selectedTier) return;
    updateUser.mutate(
      { userId, data: { subscriptionTier: selectedTier } },
      {
        onSuccess: () => {
          setSelectedTier(null);
          showStatus("success", "Subscription tier updated.");
        },
        onError: () => showStatus("error", "Failed to update tier."),
      }
    );
  }

  function handlePriceSave(priceMicroUsd: number, note: string) {
    if (!userId) return;
    updateUser.mutate(
      {
        userId,
        data: { billing: { priceMicroUsd, ...(note && { note }) } },
      },
      {
        onSuccess: () =>
          showStatus(
            "success",
            `Monthly price set to ${formatMicroUsd(priceMicroUsd)}.`
          ),
        // The API says what was wrong with the price; pass it on.
        onError: (error) =>
          showStatus(
            "error",
            getErrorMessage(error, "Failed to set the price.")
          ),
      }
    );
  }

  function handlePriceClear() {
    if (!userId) return;
    updateUser.mutate(
      { userId, data: { billing: { priceMicroUsd: null } } },
      {
        onSuccess: () =>
          showStatus(
            "success",
            "Custom price cleared. The list price applies."
          ),
        onError: () => showStatus("error", "Failed to clear the price."),
      }
    );
  }

  function handleRolesSave(
    add: PlatformRoleName[],
    remove: PlatformRoleName[],
    onDone: (success: boolean) => void
  ) {
    if (!userId) return;
    updateRoles.mutate(
      { userId, data: { add, remove } },
      {
        onSuccess: (data) => {
          onDone(true);
          showStatus(
            "success",
            data.warning
              ? `Platform roles updated. ${data.warning}`
              : "Platform roles updated."
          );
        },
        // The API says exactly what was refused and why; pass it on verbatim
        // rather than a generic message, same as the price panel above.
        onError: (error) => {
          onDone(false);
          showStatus(
            "error",
            getErrorMessage(error, "Failed to update platform roles.")
          );
        },
      }
    );
  }

  function handleConfirmAction() {
    if (!userId || !confirmAction) return;

    if (confirmAction === "disable" || confirmAction === "enable") {
      const action = confirmAction;
      disableUser.mutate(
        { userId, disabled: action === "disable" },
        {
          onSuccess: () => {
            setConfirmAction(null);
            showStatus(
              "success",
              action === "disable" ? "User disabled." : "User enabled."
            );
          },
          onError: () => showStatus("error", `Failed to ${action} user.`),
        }
      );
    } else if (confirmAction === "reset") {
      resetPassword.mutate(userId, {
        onSuccess: () => {
          setConfirmAction(null);
          showStatus("success", "Password reset email sent.");
        },
        onError: () => showStatus("error", "Failed to send password reset."),
      });
    }
  }

  function handleRecalculate() {
    if (!userId) return;
    recalculateStreaks.mutate(userId, {
      onSuccess: (data) => {
        setConfirmRecalculate(false);
        showStatus(
          "success",
          `Streaks recalculated: ${data.uniqueDaysProcessed} unique days from ${data.totalPublishedDrafts} published posts.`
        );
      },
      onError: () => showStatus("error", "Failed to recalculate streaks."),
    });
  }

  if (isLoading) {
    return (
      <div className="flex h-60 items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" pill onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back
        </Button>
        <Alert variant="error">User not found or failed to load.</Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          pill
          onClick={() => navigate("/users")}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Users
        </Button>
      </div>

      <PageHeader
        variant="classic"
        title={user.name || user.email}
        description={user.name ? user.email : undefined}
        action={
          <StatusPill
            tone={user.isActive ? "green" : "red"}
            className="gap-1.5 px-3 py-1 font-semibold"
          >
            {user.isActive ? (
              <UserCheck className="h-3.5 w-3.5" />
            ) : (
              <UserX className="h-3.5 w-3.5" />
            )}
            {user.isActive ? "Enabled" : "Disabled"}
          </StatusPill>
        }
      />

      {/* Content counts */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <CountCard
          icon={User}
          label="Personas"
          value={user.counts.personas}
          tone="violet"
        />
        <CountCard
          icon={FileText}
          label="Sources"
          value={user.counts.sources}
          tone="blue"
        />
        <CountCard
          icon={Layers}
          label="Drafts"
          value={user.counts.drafts}
          tone="cyan"
        />
        <CountCard
          icon={FileText}
          label="Total Posts"
          value={user.streaks?.totalPublishedPosts ?? 0}
          tone="emerald"
        />
        <CountCard
          icon={Flame}
          label="Weekly Streak"
          value={user.streaks?.currentWeeklyStreak ?? 0}
          tone="amber"
        />
      </div>

      <UsageAndCostPanel user={user} />

      {/* Status message */}
      {statusMessage && (
        <Alert variant={statusMessage.type === "success" ? "success" : "error"}>
          {statusMessage.text}
        </Alert>
      )}

      {/* Streaks & Gamification */}
      <div className={SECTION_CLASS}>
        <div className="flex items-center justify-between">
          <h2 className={SECTION_TITLE_CLASS}>Streaks & Gamification</h2>
          {!confirmRecalculate && (
            <Button
              size="sm"
              variant="outline"
              pill
              onClick={() => setConfirmRecalculate(true)}
              disabled={recalculateStreaks.isPending}
              className="text-xs"
            >
              <ArrowsClockwise
                className={cn(
                  "mr-1.5 h-3.5 w-3.5",
                  recalculateStreaks.isPending && "animate-spin"
                )}
              />
              Recalculate
            </Button>
          )}
        </div>
        {confirmRecalculate && (
          <div className={CONFIRM_CLASS}>
            <p className="text-sm font-medium">
              Recalculate this user&apos;s streaks from their full publishing
              history? This will overwrite current streak and gamification data.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleRecalculate}
                disabled={recalculateStreaks.isPending}
              >
                {recalculateStreaks.isPending
                  ? "Recalculating..."
                  : "Confirm Recalculate"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirmRecalculate(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Primary stats */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="flex items-center gap-2 rounded-xl bg-muted/40 px-3 py-2.5">
            <Flame className="h-4 w-4 text-orange-500 shrink-0" />
            <div>
              <p className="text-lg font-bold">
                {user.streaks?.currentWeeklyStreak ?? 0}
              </p>
              <p className="text-[11px] text-muted-foreground">
                Weekly (best: {user.streaks?.longestWeeklyStreak ?? 0})
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-xl bg-muted/40 px-3 py-2.5">
            <Zap className="h-4 w-4 text-yellow-500 shrink-0" />
            <div>
              <p className="text-lg font-bold">
                {user.streaks?.currentDailyStreak ?? 0}
              </p>
              <p className="text-[11px] text-muted-foreground">
                Daily (best: {user.streaks?.longestDailyStreak ?? 0})
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-xl bg-muted/40 px-3 py-2.5">
            <Trophy className="h-4 w-4 text-amber-500 shrink-0" />
            <div>
              <p className="text-lg font-bold">{user.gamification?.xp ?? 0}</p>
              <p className="text-[11px] text-muted-foreground">
                XP (Lv. {user.gamification?.level ?? 1})
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-xl bg-muted/40 px-3 py-2.5">
            <FileText className="h-4 w-4 text-blue-500 shrink-0" />
            <div>
              <p className="text-lg font-bold">
                {user.streaks?.totalPublishedPosts ?? 0}
              </p>
              <p className="text-[11px] text-muted-foreground">Total posts</p>
            </div>
          </div>
        </div>

        {/* Streak detail grid */}
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <div className="flex justify-between border-b border-border/30 py-1.5">
            <span className="text-muted-foreground">Daily streak started</span>
            <span>{user.streaks?.dailyStreakStartDate ?? "—"}</span>
          </div>
          <div className="flex justify-between border-b border-border/30 py-1.5">
            <span className="text-muted-foreground">Weekly streak started</span>
            <span>{user.streaks?.weeklyStreakStartDate ?? "—"}</span>
          </div>
          <div className="flex justify-between border-b border-border/30 py-1.5">
            <span className="text-muted-foreground">Last daily post</span>
            <span>{user.streaks?.lastDailyPostDate ?? "—"}</span>
          </div>
          <div className="flex justify-between border-b border-border/30 py-1.5">
            <span className="text-muted-foreground">Last weekly post</span>
            <span>{user.streaks?.lastWeeklyPostWeek ?? "—"}</span>
          </div>
          <div className="flex justify-between border-b border-border/30 py-1.5">
            <span className="text-muted-foreground">Last published at</span>
            <span>
              {user.streaks?.lastPublishedAt
                ? formatDate(user.streaks.lastPublishedAt)
                : "Never"}
            </span>
          </div>
          <div className="flex justify-between border-b border-border/30 py-1.5">
            <span className="text-muted-foreground">Bonus generations</span>
            <span>{user.gamification?.bonusGenerations ?? 0}</span>
          </div>
        </div>

        {/* Freeze status */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <StatusPill
            tone={user.streaks?.dailyFreezeActive ? "blue" : "neutral"}
            className="gap-1"
          >
            <Snowflake className="h-3 w-3" />
            Daily freeze {user.streaks?.dailyFreezeActive ? "active" : "off"}
          </StatusPill>
          <StatusPill
            tone={user.streaks?.weeklyFreezeActive ? "blue" : "neutral"}
            className="gap-1"
          >
            <Snowflake className="h-3 w-3" />
            Weekly freeze {user.streaks?.weeklyFreezeActive ? "active" : "off"}
          </StatusPill>
          {user.streakFreezes && (
            <span className="text-[11px] text-muted-foreground">
              Freezes used: {user.streakFreezes.dailyFreezesUsed ?? 0} daily /{" "}
              {user.streakFreezes.weeklyFreezesUsed ?? 0} weekly
              {user.streakFreezes.lastResetMonth
                ? ` (reset ${user.streakFreezes.lastResetMonth})`
                : ""}
            </span>
          )}
        </div>

        {/* Badges */}
        <div className="space-y-1.5 pt-1">
          <p className="text-xs font-medium text-muted-foreground">
            Badges ({user.gamification?.badges?.length ?? 0})
          </p>
          {user.gamification?.badges && user.gamification.badges.length > 0 ? (
            <div className="space-y-2">
              {(["achievement", "streak"] as const).map((group) => {
                // Badges may be string[] (legacy) or BadgeRecord[] ({ id, earnedAt })
                const badgeIds: string[] = user.gamification!.badges.map(
                  (b: unknown) =>
                    typeof b === "string" ? b : (b as { id: string }).id
                );
                const groupBadgeIds = badgeIds.filter(
                  (id) => getBadgeCategory(id) === group
                );
                if (groupBadgeIds.length === 0) return null;
                return (
                  <div key={group}>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {group === "achievement"
                        ? "Achievements"
                        : "Streak Badges"}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {groupBadgeIds.map((badgeId) => (
                        <span
                          key={badgeId}
                          title={BADGE_MAP.get(badgeId)?.description}
                        >
                          <StatusPill tone="orange">
                            {getBadgeName(badgeId)}
                          </StatusPill>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              No badges earned yet.
            </p>
          )}
        </div>

        {/* Feature unlocks */}
        {user.gamification?.unlockedFeatures &&
          user.gamification.unlockedFeatures.length > 0 && (
            <div className="space-y-1.5 pt-1">
              <p className="text-xs font-medium text-muted-foreground">
                <Star className="mr-1 inline h-3 w-3" />
                Unlocked Features
              </p>
              <div className="flex flex-wrap gap-1.5">
                {user.gamification.unlockedFeatures.map((feature: string) => (
                  <StatusPill key={feature} tone="green">
                    {feature.replace(/_/g, " ")}
                  </StatusPill>
                ))}
              </div>
            </div>
          )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Profile details column */}
        <div className="space-y-4 rounded-xl border border-border/50 bg-card p-5">
          <h2 className={SECTION_TITLE_CLASS}>Profile</h2>
          <div className="space-y-4">
            <InfoRow icon={Mail} label="Email" value={user.email} />
            <InfoRow
              icon={user.emailVerified ? MailCheck : MailX}
              label="Email Verified"
              value={
                <span
                  className={cn(
                    "text-xs font-medium",
                    user.emailVerified
                      ? "text-green-600 dark:text-green-400"
                      : "text-amber-500 dark:text-amber-400"
                  )}
                >
                  {user.emailVerified ? "Verified" : "Unverified"}
                </span>
              }
            />
            <InfoRow icon={User} label="Name" value={user.name || "—"} />
            {user.roles.length > 0 && (
              <InfoRow
                icon={Shield}
                label="Roles"
                value={
                  <div className="flex flex-wrap gap-1">
                    {user.roles.map((role: string) => (
                      <StatusPill
                        key={role}
                        tone="violet"
                        className="capitalize"
                      >
                        {role.replace("platform_", "")}
                      </StatusPill>
                    ))}
                  </div>
                }
              />
            )}
            <InfoRow
              icon={Calendar}
              label="Joined"
              value={formatDate(user.createdAt)}
            />
            <InfoRow
              icon={UsersThree}
              label="Team size"
              value={
                TEAM_SIZE_LABELS[user.signup?.teamSize ?? ""] ?? "Not answered"
              }
            />
            <InfoRow
              icon={Megaphone}
              label="Heard about Spark"
              value={user.signup?.referralSource || "Not asked yet"}
            />
            <InfoRow
              icon={Clock}
              label="Last Login"
              value={formatDate(user.lastLoginAt)}
            />
            <InfoRow
              icon={Clock}
              label="Updated"
              value={formatDate(user.updatedAt)}
            />
          </div>
        </div>

        {/* Actions column */}
        <div className="space-y-6">
          {/* Tier management – admin only */}
          {hasRole("platform_admin") && (
            <div className={SECTION_CLASS}>
              <h2 className={SECTION_TITLE_CLASS}>Subscription Tier</h2>
              <SegmentedControl<string>
                label="Subscription tier"
                value={activeTier}
                onChange={(tier) =>
                  setSelectedTier(tier === user.subscriptionTier ? null : tier)
                }
                options={TIERS.map((tier) => ({
                  value: tier,
                  label: tier.charAt(0).toUpperCase() + tier.slice(1),
                }))}
              />
              <p className="text-xs text-muted-foreground">
                An account with a subscription takes its plan from it. For a
                reasoned, audited change (a comp, a referral month), use{" "}
                <button
                  type="button"
                  className="underline"
                  onClick={() => navigate(`/billing?user=${userId}`)}
                >
                  Billing, Plan override
                </button>
                .
              </p>
              {selectedTier && selectedTier !== user.subscriptionTier && (
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    size="sm"
                    onClick={handleTierSave}
                    disabled={updateUser.isPending}
                  >
                    {updateUser.isPending ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedTier(null)}
                  >
                    Cancel
                  </Button>
                </div>
              )}
              <PricePanel
                user={user}
                onSave={handlePriceSave}
                onClear={handlePriceClear}
                isPending={updateUser.isPending}
              />
            </div>
          )}

          {/* Platform roles – admin only; non-admin viewers keep the
              read-only "Roles" row in the Profile column above. */}
          {hasRole("platform_admin") && (
            <PlatformRolesPanel
              key={user.roles.join(",")}
              user={user}
              onSave={handleRolesSave}
              isPending={updateRoles.isPending}
            />
          )}

          {/* Account actions */}
          <div className={SECTION_CLASS}>
            <h2 className={SECTION_TITLE_CLASS}>Account Actions</h2>

            {confirmAction ? (
              <div className={CONFIRM_CLASS}>
                <p className="text-sm font-medium">
                  {confirmAction === "disable" &&
                    "Disable this user? They will not be able to sign in."}
                  {confirmAction === "enable" &&
                    "Re-enable this user's account?"}
                  {confirmAction === "reset" &&
                    "Send a password reset email to this user?"}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant={
                      confirmAction === "enable" ? "default" : "destructive"
                    }
                    onClick={handleConfirmAction}
                    disabled={disableUser.isPending || resetPassword.isPending}
                  >
                    {disableUser.isPending || resetPassword.isPending
                      ? "Processing..."
                      : "Confirm"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmAction(null)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {hasRole("platform_admin") &&
                  (user.isActive ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmAction("disable")}
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <UserX className="mr-1.5 h-3.5 w-3.5" />
                      Disable Account
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmAction("enable")}
                    >
                      <UserCheck className="mr-1.5 h-3.5 w-3.5" />
                      Enable Account
                    </Button>
                  ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmAction("reset")}
                >
                  <Key className="mr-1.5 h-3.5 w-3.5" />
                  Reset Password
                </Button>
                <Link
                  to={`/reports?user=${encodeURIComponent(user.userId)}`}
                  className="inline-flex h-8 items-center rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-accent"
                >
                  View reports
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <UserCreditsPanel
        userId={user.userId}
        userLabel={user.name || user.email}
      />

      {/* Personas & recent activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className={SECTION_CLASS}>
          <h2 className={SECTION_TITLE_CLASS}>Personas</h2>
          <PersonasPanel personas={user.personas ?? []} />
        </div>
        <div className={SECTION_CLASS}>
          <h2 className={SECTION_TITLE_CLASS}>Recent Activity</h2>
          <RecentActivityPanel entries={user.recentActivity ?? []} />
        </div>
      </div>
    </div>
  );
}
